import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function SetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { setUser } = useAuth();
  const [checking, setChecking] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setError("No invitation token found in the link."); setChecking(false); return; }
    api.get(`/auth/onboarding/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "This link is invalid or has expired."))
      .finally(() => setChecking(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/set-password", { token, password });
      setUser(data);
      toast.success("Password set. Welcome to Legends!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not set password");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-navy flex items-center justify-center px-6 py-16" data-testid="set-password-page">
      <div className="relative w-full max-w-md">
        <Link to="/" className="flex flex-col items-center mb-8">
          <img src="/legends-logo.png?v=4" alt="Legends Poker" className="w-24 h-24 object-contain drop-shadow-[0_0_25px_rgba(212,175,55,0.25)]" />
        </Link>
        <div className="bg-navy-paper border border-slate-800 p-8">
          {checking ? (
            <div className="text-center text-slate-400 py-10"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-3 text-gold" />Checking your invitation…</div>
          ) : error ? (
            <div className="text-center py-6" data-testid="set-password-error">
              <p className="text-slate-300">{error}</p>
              <Link to="/become-a-legend" className="text-gold hover:underline text-sm mt-4 inline-block">Apply to become a Legend →</Link>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <KeyRound className="w-10 h-10 text-gold mx-auto mb-3" />
                <h1 className="font-display text-2xl font-bold text-white">Create Your Password</h1>
                <p className="text-slate-400 text-sm mt-1">{info?.name ? `Welcome, ${info.name.split(" ")[0]}! ` : ""}Set a password for <span className="text-gold">{info?.email}</span></p>
              </div>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label className="text-slate-300">New Password</Label>
                  <Input required type="password" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 rounded-none bg-navy border-slate-700" data-testid="set-password-input" />
                </div>
                <div>
                  <Label className="text-slate-300">Confirm Password</Label>
                  <Input required type="password" minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5 rounded-none bg-navy border-slate-700" data-testid="set-password-confirm" />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-gold text-navy hover:bg-gold-light rounded-none font-bold uppercase tracking-widest py-6" data-testid="set-password-submit">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Set Password & Log In"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
